import { useQuery, useMutation } from 'react-query'
import { fetchAPI } from '@/lib/api'
import { useFilterContext } from '@/features/filters/context'
import { Dispatch, SetStateAction, useState }from 'react'
import { useToast } from '@chakra-ui/react'
import { t } from '@/lib/helpers'
import { ERROR_TOAST_CONFIG, TOAST_CONFIG } from '@/lib/constants'
import {
  ADMIN_PAGE_VIEWS_API,
  ADMIN_PAGE_VIEWS_QUERY_KEYS,
  ADMIN_PAGE_VIEWS_CONFIG,
} from '../constants'

const getAdminCompanies = async () => {
  const response = await fetchAPI({
    path: ADMIN_PAGE_VIEWS_API.COMPANIES
  });

  return response.data;
};

const getAdminProjectOptions = async (companyId: number) => {
  const response = await fetchAPI({
    path: `${ADMIN_PAGE_VIEWS_API.PROJECTS}?company_id=${companyId}`
  });

  return response.data;
};

const getAdminPageViewsData = async (params: any) => {
  const response = await fetchAPI({
    path: ADMIN_PAGE_VIEWS_API.RETRIEVE_DATA, params
  });

  return response.data;
};

const createAdminExportJob = async (payload: any) => {
  const response = await fetchAPI({
    path: ADMIN_PAGE_VIEWS_API.EXPORT, method: 'post', data: payload
  });

  return response.data;
};

const getAdminExportStatus = async (companyId: number) => {
  const response = await fetchAPI({
    path: `${ADMIN_PAGE_VIEWS_API.EXPORT}?company_id=${companyId}`
  });

  return response.data;
};


export const useAdminCompanies = () => {
  return useQuery(
    ADMIN_PAGE_VIEWS_QUERY_KEYS.COMPANIES,
    getAdminCompanies
  );
};

export const useAdminProjectOptions = (companyId: number | null) => {
  return useQuery(
    [ADMIN_PAGE_VIEWS_QUERY_KEYS.PROJECT_OPTIONS, companyId],
    () => getAdminProjectOptions(companyId!),
    { enabled: !!companyId }
  );
};

export const useAdminPageViews = () => {
  const { meta } = useFilterContext();
  const params = {
    company_id: meta.company_id,
    start_date: meta.start_date,
    end_date: meta.end_date,
    url: meta.url || [],
    page: meta.page,
    per: ADMIN_PAGE_VIEWS_CONFIG.DEFAULT_PER_PAGE,
  };

  const canFetch = !!(meta.company_id && meta.start_date && meta.end_date && meta.url?.length);

  return useQuery(
    [ADMIN_PAGE_VIEWS_QUERY_KEYS.PAGE_VIEWS, params],
    () => getAdminPageViewsData(params),
    { enabled: canFetch }
  );
};

const usePolling = ({
  start,
  setStart,
  companyId
}: {
  start: boolean;
  setStart: Dispatch<SetStateAction<boolean>>;
  companyId: number | null;
}) => {
  const toast = useToast();

  return useQuery({
    queryKey: [ADMIN_PAGE_VIEWS_QUERY_KEYS.EXPORT_STATUS, companyId],
    queryFn: () => getAdminExportStatus(companyId!),
    onSuccess: (responseData) => {
      if (!responseData) return;

      const { status, meta } = responseData;

      if (status === 'succeeded') {
        const downloadUrl = meta?.download_url;

        if (downloadUrl) {
          const a = document.createElement('a');
          a.href = downloadUrl;
          a.click();

          toast({
            ...TOAST_CONFIG,
            description: t('views.features.packages.page_views.success_download')
          });
        }

        setStart(false);
      } else if (status === 'failed') {
        toast({
          ...ERROR_TOAST_CONFIG,
          description: t('views.features.packages.page_views.failed_download')
        });

        setStart(false);
      }
    },
    enabled: start && !!companyId,
    refetchInterval: start ? ADMIN_PAGE_VIEWS_CONFIG.POLLING_INTERVAL_MS : false,
    refetchOnWindowFocus: false,
  });
};

export const useAdminPollingPageViewCsv = () => {
  const [startPolling, setStartPolling] = useState(false);
  const toast = useToast();
  const { meta } = useFilterContext();
  const companyId = meta.company_id as number | null;

  const { status, data: jobData } = usePolling({ start: startPolling, setStart: setStartPolling, companyId });
  const lastJobStatus = jobData?.data;

  const { mutate, isLoading: isCreatingJob } = useMutation(createAdminExportJob, {
    onSuccess: () => { setStartPolling(true); },
    onError: () => {
      toast({
        ...ERROR_TOAST_CONFIG,
        description: t('views.features.packages.page_views.failed_download')
      });
    },
  });

  return {
    startExport: mutate,
    isLoading: isCreatingJob || startPolling,
    lastJobStatus
  };
};
